// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.
import { Controller } from "@hotwired/stimulus"

// Node resizer — drag handles to resize a node.
export default class extends Controller {
  static outlets = ["ui--flow"]

  static values = {
    nodeId: { type: String, default: "" },
    minWidth: { type: Number, default: 50 },
    minHeight: { type: Number, default: 30 },
    maxWidth: { type: Number, default: 2000 },
    maxHeight: { type: Number, default: 2000 },
    keepAspectRatio: { type: Boolean, default: false }
  }

  connect() {
    this.element.classList.add("ui-flow-node-resizer")
    this._activeMove = null
    this._activeUp = null
    this._createHandles()
  }

  // S13: a mid-gesture disconnect used to leak the document-level
  // pointermove/pointerup pair (they were only ever removed on pointerup).
  disconnect() {
    this._removeGestureListeners()
  }

  _createHandles() {
    const positions = ["se", "sw", "ne", "nw", "n", "s", "e", "w"]
    positions.forEach(pos => {
      const handle = document.createElement("div")
      handle.className = `ui-flow-resize-handle ui-flow-resize-handle--${pos}`
      handle.dataset.resizePosition = pos
      handle.style.cssText = this._getHandleStyle(pos)
      handle.addEventListener("pointerdown", (e) => this._startResize(e, pos))
      this.element.appendChild(handle)
    })
  }

  _getHandleStyle(pos) {
    const base = "position:absolute;width:10px;height:10px;z-index:20;"
    const styles = {
      se: base + "right:-5px;bottom:-5px;cursor:se-resize;",
      sw: base + "left:-5px;bottom:-5px;cursor:sw-resize;",
      ne: base + "right:-5px;top:-5px;cursor:ne-resize;",
      nw: base + "left:-5px;top:-5px;cursor:nw-resize;",
      n: base + "top:-5px;left:50%;transform:translateX(-50%);cursor:n-resize;width:30px;height:5px;",
      s: base + "bottom:-5px;left:50%;transform:translateX(-50%);cursor:s-resize;width:30px;height:5px;",
      e: base + "right:-5px;top:50%;transform:translateY(-50%);cursor:e-resize;width:5px;height:30px;",
      w: base + "left:-5px;top:50%;transform:translateY(-50%);cursor:w-resize;width:5px;height:30px;"
    }
    return styles[pos] || base
  }

  // The engine, for zoom + geometry writeback: the outlet the component emits
  // (node_resizer_component.html.erb), else the enclosing canvas's controller.
  _flow() {
    try { if (this.hasUiFlowOutlet) return this.uiFlowOutlet } catch (e) { }
    try {
      const canvas = this.element.closest("[data-controller~='ui--flow']")
      if (canvas && this.application) {
        return this.application.getControllerForElementAndIdentifier(canvas, "ui--flow") || null
      }
    } catch (e) { }
    return null
  }

  // F5: pointer deltas are screen pixels but style width/height/left/top are
  // flow units inside the zoomed pane — at zoom 0.5 a 100px screen drag is a
  // 200-unit resize. Without the division the handle outruns the cursor.
  _zoom() {
    const z = this._flow()?.viewport?.zoom
    return (typeof z === "number" && Number.isFinite(z) && z > 0) ? z : 1
  }

  _nodeId(nodeEl) {
    return this.nodeIdValue || nodeEl?.dataset?.["ui-FlowNode"] || ""
  }

  _startResize(event, pos) {
    event.stopPropagation(); event.preventDefault()
    const nodeEl = this.element.closest("[data-ui--flow-node]")
    if (!nodeEl) return
    this._removeGestureListeners() // a previous interrupted gesture must not stack

    const zoom = this._zoom()
    const nodeId = this._nodeId(nodeEl)
    const startX = event.clientX, startY = event.clientY
    // offsetWidth/Height are LAYOUT units — CSS transforms on the pane do not
    // affect them, so they are already flow units. Only the pointer deltas
    // are screen pixels and need the /zoom conversion (F5).
    const startW = nodeEl.offsetWidth, startH = nodeEl.offsetHeight
    const startLeft = parseFloat(nodeEl.style.left) || 0
    const startTop = parseFloat(nodeEl.style.top) || 0
    const aspect = startH ? startW / startH : 1
    // Last computed flow-unit geometry — the resizeend writeback reads this
    // (offsetWidth is screen px, and 0 under jsdom).
    let geom = { width: startW, height: startH, x: startLeft, y: startTop }

    const onMove = (e) => {
      let dx = (e.clientX - startX) / zoom, dy = (e.clientY - startY) / zoom
      let newW = startW, newH = startH, newLeft = startLeft, newTop = startTop

      if (pos.includes("e")) newW = Math.max(this.minWidthValue, Math.min(this.maxWidthValue, startW + dx))
      if (pos.includes("w")) { newW = Math.max(this.minWidthValue, Math.min(this.maxWidthValue, startW - dx)); newLeft = startLeft + startW - newW }
      if (pos.includes("s")) newH = Math.max(this.minHeightValue, Math.min(this.maxHeightValue, startH + dy))
      if (pos.includes("n")) { newH = Math.max(this.minHeightValue, Math.min(this.maxHeightValue, startH - dy)); newTop = startTop + startH - newH }

      if (this.keepAspectRatioValue) {
        if (pos === "e" || pos === "w") newH = newW / aspect
        else if (pos === "n" || pos === "s") newW = newH * aspect
        else { newH = newW / aspect }
        // A3: the derivation runs AFTER the primary clamp, so the derived
        // dimension can escape its own [min, max] band (SE drag clamped at
        // maxWidth with a wide aspect → newH > maxHeight). Clamp the derived
        // dimension back into its band — ONE pass, deliberately not an
        // iterate-to-fixpoint loop: re-deriving the primary from the freshly
        // clamped secondary would break the primary's own clamp and the two
        // would ping-pong; a single clamp always terminates and the primary
        // dimension keeps its clamped (correct) value.
        if (pos === "n" || pos === "s") newW = Math.max(this.minWidthValue, Math.min(this.maxWidthValue, newW))
        else newH = Math.max(this.minHeightValue, Math.min(this.maxHeightValue, newH))
      }

      geom = { width: newW, height: newH, x: newLeft, y: newTop }
      nodeEl.style.width = `${newW}px`; nodeEl.style.height = `${newH}px`
      nodeEl.style.left = `${newLeft}px`; nodeEl.style.top = `${newTop}px`

      const detail = { nodeId, width: newW, height: newH, x: newLeft, y: newTop }
      this.dispatch("resize", { detail })
      document.dispatchEvent(new CustomEvent("ui--flow:noderesize", { detail, bubbles: true }))
    }

    const onUp = () => {
      this._removeGestureListeners()
      // F5 writeback: the engine's node def must carry the resized geometry or
      // toObject()/save persist the pre-resize box and any undo snaps an
      // NW-resized node back to its stale map position. updateNode owns
      // width/height; moveNode owns position (emits nodemove so edges follow).
      // WHY no _pushUndo here (deliberate asymmetry with node drags, which do
      // push): hosts compose resize into their own save/undo flows — the
      // gesture streams geometry events the host may throttle/coalesce — so
      // the resizer stays undo-neutral. Drag-undo parity is a parked product
      // decision, not an oversight; do not "fix" it silently.
      const flow = this._flow()
      if (flow && nodeId) {
        flow.updateNode?.(nodeId, { width: geom.width, height: geom.height })
        if (geom.x !== startLeft || geom.y !== startTop) flow.moveNode?.(nodeId, geom.x, geom.y)
      }
      const detail = { nodeId, width: geom.width, height: geom.height, x: geom.x, y: geom.y }
      this.dispatch("resizeend", { detail })
      document.dispatchEvent(new CustomEvent("ui--flow:noderesizeend", { detail, bubbles: true }))
    }

    this._activeMove = onMove
    this._activeUp = onUp
    document.addEventListener("pointermove", onMove)
    document.addEventListener("pointerup", onUp)
    this.dispatch("resizestart", { detail: { nodeId } })
    document.dispatchEvent(new CustomEvent("ui--flow:noderesizestart", { detail: { nodeId }, bubbles: true }))
  }

  _removeGestureListeners() {
    if (this._activeMove) { document.removeEventListener("pointermove", this._activeMove); this._activeMove = null }
    if (this._activeUp) { document.removeEventListener("pointerup", this._activeUp); this._activeUp = null }
  }
}
